import { useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { Notation } from "@/components/Notation";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 24 — Diatonic Seventh Chords on Piano.
 *
 * Build all seven seventh chords from a major scale by stacking thirds.
 * Each diatonic position has its own quality:
 *   I    = Maj7
 *   ii   = m7
 *   iii  = m7
 *   IV   = Maj7
 *   V    = Dom7
 *   vi   = m7
 *   vii  = m7♭5 (half-diminished)
 *
 * Bespoke visual: the 7-Stack Atlas. A horizontal row of seven mini chord
 * stacks (one per scale degree). Each stack shows four voices, colour-coded
 * by quality. Click a stack → hear it. The Maj7s are amber, the m7s are
 * cyan, the dom7 is rose, the half-diminished is violet. The rule pattern
 * (Maj7 m7 m7 Maj7 Dom7 m7 m7♭5) becomes obvious at a glance.
 */

interface SeventhChord {
  degree: number;
  roman: string;
  quality: "Maj7" | "m7" | "Dom7" | "m7♭5";
  /** Semitone offsets from tonic for the four voices, root → 7th. */
  offsets: [number, number, number, number];
}

const SEVENTHS: SeventhChord[] = [
  { degree: 1, roman: "I",      quality: "Maj7",   offsets: [0, 4, 7, 11]  },
  { degree: 2, roman: "ii",     quality: "m7",     offsets: [2, 5, 9, 12]  },
  { degree: 3, roman: "iii",    quality: "m7",     offsets: [4, 7, 11, 14] },
  { degree: 4, roman: "IV",     quality: "Maj7",   offsets: [5, 9, 12, 16] },
  { degree: 5, roman: "V",      quality: "Dom7",   offsets: [7, 11, 14, 17] },
  { degree: 6, roman: "vi",     quality: "m7",     offsets: [9, 12, 16, 19] },
  { degree: 7, roman: "vii",    quality: "m7♭5",   offsets: [11, 14, 17, 21] },
];

const QUALITY_TONE: Record<SeventhChord["quality"], { fill: string; ring: string; text: string; chip: string; description: string }> = {
  "Maj7":  { fill: "from-amber-400 to-amber-600",   ring: "ring-amber-300/60",  text: "text-amber-100",  chip: "bg-amber-500/20 text-amber-200",   description: "Lush, restful. The signature jazz I and IV." },
  "m7":    { fill: "from-cyan-400 to-cyan-600",     ring: "ring-cyan-300/60",   text: "text-cyan-100",   chip: "bg-cyan-500/20 text-cyan-200",     description: "Mellow, neutral. Lives on ii, iii and vi." },
  "Dom7":  { fill: "from-rose-400 to-rose-600",     ring: "ring-rose-300/60",   text: "text-rose-100",   chip: "bg-rose-500/20 text-rose-200",     description: "Tense, leading. The chord that wants to resolve." },
  "m7♭5":  { fill: "from-violet-400 to-violet-600", ring: "ring-violet-300/60", text: "text-violet-100", chip: "bg-violet-500/20 text-violet-200", description: "Half-diminished. A very tense, unique sound." },
};

export default function PianoIntermediate24() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "24");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="G" availableQualities={["major"]}>
        <PIShell
          title="Diatonic Seventh Chords"
          exerciseGoal="Stack four notes per chord, all from one scale. Seven diatonic 7ths, four qualities, one fixed pattern."
          bpm={84}
          difficulty="Advanced"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [chordIdx, setChordIdx] = useState(0);
  const chord = SEVENTHS[chordIdx];

  const masterHighlights: KeyHighlight[] = chord.offsets.map((off, i) => ({
    pc: ((k.tonicPc + off) % 12 + 12) % 12,
    tone: i === 0 ? "root" : "voice",
    label: degreeLabel(off - chord.offsets[0]),
  }));

  const playChord = async () => {
    kickAudio();
    const notes = chord.offsets.map((off) =>
      midiToPitchName(tonicMidi + off, k.useFlats)
    );
    await playSequence([{ time: 0, notes, duration: "1n" }], 84);
  };

  const playArpeggio = async () => {
    kickAudio();
    const sec = 60 / 84 / 2;
    const events = chord.offsets.map((off, i) => ({
      time: i * sec,
      notes: [midiToPitchName(tonicMidi + off, k.useFlats)],
      duration: "8n",
    }));
    await playSequence(events, 84);
  };

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={masterHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel={`The active diatonic seventh chord`}
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Master keyboard reflects the active chord's four voices. Click
          any stack in the atlas below to switch.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="Add one note. Welcome to jazz.">
          <p>
            A seventh chord is a triad plus one more note — the seventh
            scale degree above the chord's root. That single addition
            transforms the sound:
          </p>
          <PIQuote attribution="Jason · lesson video">
            "First of all, just to observe the sound at face value, you think
            that this is a very sophisticated sound, right? It's almost like a
            theme on its own."
          </PIQuote>
          <p>
            Build a seventh chord on every scale degree of a major scale,
            and you get <strong>seven distinct flavours</strong> — only
            four qualities, in a fixed pattern — the chords jazz music uses a
            lot.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The atlas" title="Seven stacks, four colours">
          <p>
            Click any stack to set it active. The pattern is fixed for
            every major scale: <strong>Maj7 · m7 · m7 · Maj7 · Dom7 · m7 ·
            m7♭5</strong>. Memorise the colour rhythm and you can name any
            diatonic seventh chord in any key, instantly.
          </p>
          <SeventhAtlas
            chords={SEVENTHS}
            activeIdx={chordIdx}
            onPick={setChordIdx}
            tonicPc={k.tonicPc}
            useFlats={k.useFlats}
            nameForPc={k.nameForPc}
          />
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            <div className={`rounded-xl border p-3 ${QUALITY_TONE[chord.quality].ring} bg-zinc-900/60`}>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
                    Active chord
                  </div>
                  <div className={`mt-1 text-2xl font-bold ${QUALITY_TONE[chord.quality].text}`}>
                    {k.nameForPc(k.pcFromDegree(chord.degree))}{chord.quality}
                  </div>
                </div>
                <div className={`rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${QUALITY_TONE[chord.quality].chip}`}>
                  {chord.roman}
                </div>
              </div>
              <p className="mt-1 text-[12px] leading-snug text-cyan-200/85">
                {QUALITY_TONE[chord.quality].description}
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={playChord}
                  className="rounded-lg bg-amber-400 px-3 py-1.5 text-[12px] font-bold text-zinc-900 hover:bg-amber-300"
                >
                  ▶ Block
                </button>
                <button
                  onClick={playArpeggio}
                  className="rounded-lg border border-amber-400/60 bg-amber-500/10 px-3 py-1.5 text-[12px] font-bold text-amber-200 hover:bg-amber-500/20"
                >
                  ▶ Arpeggio
                </button>
              </div>
            </div>
            <div className="rounded-xl border border-cyan-400/30 bg-cyan-950/40 p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">
                Pattern (any major scale)
              </div>
              <div className="mt-1 grid grid-cols-7 gap-1 font-mono text-[11px]">
                {SEVENTHS.map((s, i) => (
                  <div
                    key={s.degree}
                    className={`rounded p-1 text-center ${QUALITY_TONE[s.quality].chip} ${
                      i === chordIdx ? "ring-2 ring-amber-300" : ""
                    }`}
                  >
                    <div className="text-[10px] font-bold">{s.roman}</div>
                    <div className="text-[9px] opacity-80">{s.quality}</div>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] leading-snug text-cyan-200/85">
                Maj7 · m7 · m7 · Maj7 · Dom7 · m7 · m7♭5. This sequence
                holds for every major scale.
              </p>
            </div>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill label="On the staff" title="The seven 7ths as engraved music">
          <p>
            For students learning to read, here is exactly how the seven
            chords look on a treble-clef staff in the chosen key. Notice
            how each chord is a clean stack of thirds — every voice sits
            on a line OR every voice sits on a space, never mixed.
          </p>
          <DiatonicSeventhsScore tonicMidi={tonicMidi} useFlats={k.useFlats} />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="The four qualities" title="One sentence each">
          <ul className="my-3 space-y-2 text-[14px] text-cyan-50/95">
            <li>
              <strong className="text-amber-200">Maj7</strong> — major
              triad + a major-7th on top. Sounds <em>open and restful</em>,
              like late-night radio. Lives on the <strong>I</strong> and
              <strong> IV</strong> chords.
            </li>
            <li>
              <strong className="text-cyan-200">m7</strong> — minor
              triad + a minor-7th. Sounds <em>mellow and rolling</em>,
              never sad-sad. Lives on <strong>ii</strong>,
              <strong> iii</strong>, and <strong>vi</strong>.
            </li>
            <li>
              <strong className="text-rose-200">Dom7</strong> — major
              triad + a minor-7th. The <em>tension chord</em>. Wants to
              pull to I. Lives only on <strong>V</strong>.
            </li>
            <li>
              <strong className="text-violet-200">m7♭5 (half-dim)</strong>
              {" "}— diminished triad + a minor-7th. The <em>uneasy</em>{" "}
              chord — Jason calls it very tense. Lives only on{" "}
              <strong>vii</strong>.
            </li>
          </ul>
          <PIInsight>
            <p>
              Half-diminished is the chord with the slashed-o symbol{" "}
              <strong>ø</strong> on chord charts (e.g. Bø7). Fully
              diminished gets <strong>°</strong> (B°7) — that's a different
              chord, with a double-flat 7. We'll meet it in another lesson.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="The 2-5-1" title="The progression jazz loves">
          <p>
            Once you know the seven diatonic 7ths, you can play the
            progression jazz uses all the time:{" "}
            <strong>ii⁷ → V⁷ → IMaj⁷</strong>. Tense pull, release. In
            G major: <em>Am⁷ → D⁷ → GMaj⁷</em>. Click ii, then V, then I
            in the atlas above and play each chord to hear the cadence.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "251 cadences are very very commonly used in jazz music especially
            because of these extensions. The extensions makes a 251 sound
            beautiful and you'll find that in lot of songs, lot of jazz
            standards."
          </PIQuote>
          <p>His example: <em>Fly Me to the Moon</em>.</p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Reading chart symbols" title="The notation zoo">
          <p>
            Different chart writers spell seventh chords in different ways:
          </p>
          <ul className="my-3 space-y-1 font-mono text-[13px] text-cyan-100/95">
            <li>· <strong>Maj7</strong>: GMaj7, Gma7, Gmaj7, G△, G△7</li>
            <li>· <strong>m7</strong>: Am7, Ami7, Amin7, A−7</li>
            <li>· <strong>Dom7</strong>: D7 (the lonely one — no qualifier)</li>
            <li>· <strong>m7♭5</strong>: F♯ø7, F♯m7♭5, F♯half-dim</li>
          </ul>
          <p className="text-[13px] text-cyan-200/85">
            Every spelling in a row means the same chord. Use
            whichever your local chart writer prefers.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="The seven diatonic 7ths in your chosen key">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="24"
            label={`Diatonic 7ths · ${k.tonicName} major`}
            originalBpm={84}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={
              <>
                Stack first, then sing, then arpeggiate, then chain. Don't
                jump to the 2-5-1 on rung one — your hands need to know
                each chord on its own first.
              </>
            }
            rungs={[
              { title: "Stack all seven blocks",      minutes: 4, accent: "amber",   detail: "Play each chord I → vii in turn. Block voicing, all four notes. Hold each chord for two seconds. Listen." },
              { title: "Name them out loud",           minutes: 4, accent: "cyan",    detail: "Play, then SAY: 'G major seven, A minor seven, B minor seven, C major seven, D dominant seven…' Vocalise the names." },
              { title: "Arpeggiate ascending",         minutes: 4, accent: "rose",    detail: "Play each chord as 1-3-5-7-5-3-1. The arpeggio teaches the four-voice spacing in your fingers." },
              { title: "Walk the 2-5-1",                minutes: 4, accent: "emerald", detail: "Play ii⁷ → V⁷ → IMaj⁷ four times. Notice how V⁷ pulls and IMaj⁷ relaxes. Try voice-leading: keep the common tone steady where possible." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: engraved staff render of the seven diatonic 7ths ── */
function DiatonicSeventhsScore({ tonicMidi, useFlats }: { tonicMidi: number; useFlats: boolean }) {
  // Each chord = 7 measures, each measure = one whole-note 4-voice chord
  const SHARP = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
  const FLAT = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];
  const nameOf = (midi: number) => {
    const pc = ((midi % 12) + 12) % 12;
    const oct = Math.floor(midi / 12) - 1;
    return `${(useFlats ? FLAT : SHARP)[pc]}${oct}`;
  };
  // Place each chord starting one octave below middle C so the 7th sits
  // around the top of the staff, not above it.
  const measures = SEVENTHS.map((chord) => {
    const baseMidi = tonicMidi + chord.offsets[0]; // root in melody register
    const voices = chord.offsets.map((off) => tonicMidi + off);
    return {
      label: chord.roman,
      chords: [
        {
          keys: voices.map(nameOf),
          duration: "w",
        },
      ],
    };
  });
  return (
    <div className="my-3">
      <Notation
        measures={measures}
        timeSig="4/4"
        width={1280}
        height={200}
      />
      <div className="-mt-3 grid grid-cols-7 gap-1 px-3 font-mono text-[11px] text-cyan-300/70">
        {SEVENTHS.map((s) => (
          <div key={s.roman} className="text-center">
            <div className="font-bold text-cyan-100">{s.roman}</div>
            <div className="text-[10px]">{s.quality}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Bespoke visual: SeventhAtlas — seven mini stacks ── */
function SeventhAtlas({
  chords,
  activeIdx,
  onPick,
  tonicPc,
  useFlats,
  nameForPc,
}: {
  chords: SeventhChord[];
  activeIdx: number;
  onPick: (i: number) => void;
  tonicPc: number;
  useFlats: boolean;
  nameForPc: (pc: number) => string;
}) {
  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-3">
      <div className="grid grid-cols-7 gap-2">
        {chords.map((c, i) => {
          const isActive = i === activeIdx;
          const tone = QUALITY_TONE[c.quality];
          // Voice names from bottom (root) to top (7th)
          const voiceNames = c.offsets.map((off) =>
            nameForPc(((tonicPc + off) % 12 + 12) % 12)
          );
          return (
            <motion.button
              key={c.degree}
              layout
              onClick={() => onPick(i)}
              className={`flex flex-col items-stretch rounded-xl border p-2 transition-all ${
                isActive
                  ? `border-amber-300 bg-amber-500/10 ring-2 ${tone.ring}`
                  : "border-cyan-400/20 bg-zinc-900/40 hover:bg-cyan-500/5"
              }`}
            >
              {/* Roman numeral header */}
              <div className={`mb-1 rounded-md px-1.5 py-0.5 text-center text-[11px] font-mono font-bold ${tone.chip}`}>
                {c.roman}
              </div>
              {/* Voice stack — 4 voices, top→bottom */}
              <div className="flex flex-col-reverse gap-0.5">
                {voiceNames.map((vn, vi) => (
                  <motion.div
                    key={vi}
                    layout
                    className={`rounded px-1 py-1 text-center text-[10px] font-mono font-semibold ${
                      vi === voiceNames.length - 1
                        ? `bg-gradient-to-b ${tone.fill} text-zinc-900`
                        : "bg-zinc-800 text-cyan-200"
                    }`}
                  >
                    {vn}
                  </motion.div>
                ))}
              </div>
              {/* Quality chip */}
              <div className={`mt-1 rounded-md px-1.5 py-0.5 text-center text-[10px] font-bold ${tone.chip}`}>
                {c.quality}
              </div>
            </motion.button>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-wider text-cyan-300/70">
        {(["Maj7", "m7", "Dom7", "m7♭5"] as const).map((q) => (
          <span key={q} className="flex items-center gap-1.5">
            <span className={`inline-block h-3 w-3 rounded-sm bg-gradient-to-b ${QUALITY_TONE[q].fill}`} />
            <span>{q}</span>
          </span>
        ))}
      </div>
      <p className="mt-2 text-[12px] italic text-cyan-300/70">
        The top voice of each stack is the chord's 7th (its colour band).
        The bottom voice is its root.
      </p>
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}

/**
 * Name a chord tone from its actual distance above the root, so a m7♭5 shows
 * "♭5" and "♭7" rather than borrowing the labels of a major 7th chord.
 */
function degreeLabel(semitonesAboveRoot: number): string {
  switch (((semitonesAboveRoot % 12) + 12) % 12) {
    case 0:  return "1";
    case 1:  return "♭2";
    case 2:  return "2";
    case 3:  return "♭3";
    case 4:  return "3";
    case 5:  return "4";
    case 6:  return "♭5";
    case 7:  return "5";
    case 8:  return "♭6";
    case 9:  return "6";
    case 10: return "♭7";
    default: return "7";
  }
}
